import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Invoice } from '@hero/schema';

/** Valid column keys for the invoice table */
export type InvoiceColumnKey =
  | 'ref'
  | 'status'
  | 'total'
  | 'due'
  | 'period'
  // Bilateral parties — `buyer`/`seller` are the new names. `payer`/`receiver`
  // remain as aliases until all consumer column configs have been migrated.
  | 'payer'
  | 'receiver'
  | 'buyer'
  | 'seller'
  | 'remaining'
  | 'vat_inv'
  | 'actions';

export interface InvoiceTableColumn {
  key: InvoiceColumnKey;
  label?: string;
  sortable?: boolean;
  className?: string;
  visible?: boolean;
  /**
   * Custom render function for the column.
   * If not provided, InvoiceTableCell uses its default rendering.
   */
  render?: (invoice: Invoice, displayForUser: 'customer' | 'carrier') => React.ReactNode;
}

export interface UseInvoiceTableColumnsConfig {
  displayForUser: 'customer' | 'carrier';
  customColumns?: InvoiceTableColumn[];
  includeActions?: boolean;
}

/**
 * Default column translation keys — maps to `m-payment:invoice.fields.*`
 */
export const defaultInvoiceColumnLabels: Record<InvoiceColumnKey, string> = {
  ref: 'invoice.fields.reference',
  status: 'invoice.fields.status',
  total: 'invoice.fields.total_amount',
  due: 'invoice.fields.due',
  period: 'invoice.fields.period',
  payer: 'invoice.fields.buyer',
  receiver: 'invoice.fields.seller',
  buyer: 'invoice.fields.buyer',
  seller: 'invoice.fields.seller',
  remaining: 'invoice.fields.remaining',
  vat_inv: 'invoice.fields.vat_invoice',
  actions: '',
};

/**
 * Default column classNames for proper sizing and layout.
 */
const defaultInvoiceColumnClassNames: Partial<Record<InvoiceColumnKey, string>> = {
  ref: 'whitespace-nowrap',
  status: 'whitespace-nowrap',
  due: 'whitespace-nowrap',
  period: 'whitespace-nowrap',
  vat_inv: 'whitespace-nowrap',
  actions: 'whitespace-nowrap',
};

/**
 * Default columns for customer view:
 * ref | status | total | due | period | actions
 *
 * The VAT-invoice column was removed from the default set — the
 * proof thumbnail now lives inside `InvoiceDrawer > Summary` so the
 * table can stay narrow on smaller widths. Pass `customColumns`
 * including `'vat_inv'` to restore it on a specific surface.
 */
const getCustomerDefaultColumns = (): InvoiceTableColumn[] => [
  { key: 'ref', sortable: false, visible: true },
  { key: 'status', sortable: false, visible: true },
  { key: 'total', sortable: false, visible: true },
  { key: 'due', sortable: false, visible: true },
  { key: 'period', sortable: false, visible: true },
];

/**
 * Default columns for carrier view:
 * ref | status | total | due | period | actions (record-payment + finance + view)
 *
 * Same VAT-column drop as the customer defaults.
 */
const getCarrierDefaultColumns = (): InvoiceTableColumn[] => [
  { key: 'ref', sortable: false, visible: true },
  { key: 'status', sortable: false, visible: true },
  { key: 'total', sortable: false, visible: true },
  { key: 'due', sortable: false, visible: true },
  { key: 'period', sortable: false, visible: true },
];

/**
 * Hook for managing Invoice Table columns with default configurations.
 *
 * Provides automatic column setup based on user role (customer/carrier)
 * while allowing full customization.
 *
 * @example Simple — just use role defaults
 * ```tsx
 * const { columns } = useInvoiceTableColumns({ displayForUser: 'customer' });
 * ```
 *
 * @example With custom columns
 * ```tsx
 * const { columns } = useInvoiceTableColumns({
 *   displayForUser: 'customer',
 *   customColumns: [
 *     { key: 'ref' },
 *     { key: 'payer' },
 *     { key: 'total' },
 *     { key: 'status' },
 *   ],
 * });
 * ```
 */
export const useInvoiceTableColumns = ({
  displayForUser,
  customColumns,
  includeActions = true,
}: UseInvoiceTableColumnsConfig) => {
  const { t } = useTranslation('m-payment');

  const defaultColumns = useMemo((): InvoiceTableColumn[] => {
    return displayForUser === 'customer'
      ? getCustomerDefaultColumns()
      : getCarrierDefaultColumns();
  }, [displayForUser]);

  const columns = useMemo(() => {
    const cols = customColumns || defaultColumns;

    // Add actions column if includeActions is true and it's not already present
    const hasActionsColumn = cols.some(col => col.key === 'actions');
    const finalCols = [...cols];

    if (includeActions && !hasActionsColumn) {
      finalCols.push({
        key: 'actions',
        sortable: false,
        visible: true,
      });
    }

    // Auto-translate labels and apply default classNames. Note we
    // resolve the default label by reading from
    // `defaultInvoiceColumnLabels` first — an entry of `''` (e.g.
    // `actions`) signals "render an empty header"; we must NOT
    // fall back to the column key in that case (which would print
    // the literal "actions" in the header row).
    return finalCols
      .filter(col => col.visible !== false)
      .map(col => {
        const defaultLabelKey = defaultInvoiceColumnLabels[col.key];
        // explicit empty default → keep header blank
        const label =
          col.label !== undefined
            ? col.label
            : defaultLabelKey === ''
              ? ''
              : t(defaultLabelKey, col.key);
        return {
          ...col,
          label,
          className: col.className || defaultInvoiceColumnClassNames[col.key] || '',
        };
      });
  }, [customColumns, defaultColumns, includeActions, t]);

  return {
    columns,
    defaultColumns,
  };
};
